/**
 * Control Portal — Platform Users
 * Manages Track0tic internal staff (admins, support, engineers).
 * Distinct from per-customer user management.
 */
import { useState, useEffect } from 'react';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { toast } from 'sonner';
import { useControlTheme } from '@/contexts/ControlThemeContext';
import { cn } from '@/lib/utils';
import {
  Users, UserPlus, Edit2, Trash2, Loader2, ShieldCheck, RefreshCw, Search
} from 'lucide-react';

const blankForm = { full_name: '', email: '', phone: '', password: '', status: 'active' };

export default function PlatformUsersPage() {
  const { isDark } = useControlTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await controlApi.getPlatformUsers();
      setUsers(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load platform users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm);
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      full_name: u.full_name,
      email: u.email || '',
      phone: u.phone || '',
      password: '',
      status: u.status || 'active',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.full_name?.trim() || !form.email?.trim()) {
      toast.error('Full name and email are required');
      return;
    }
    if (!editing && !form.password) {
      toast.error('Password is required for new users');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          full_name: form.full_name,
          phone: form.phone || null,
          status: form.status,
          ...(form.password ? { password: form.password } : {}),
        };
        await controlApi.updatePlatformUser(editing.id, payload);
        toast.success('Platform user updated');
      } else {
        await controlApi.createPlatformUser({
          full_name: form.full_name,
          email: form.email,
          phone: form.phone || null,
          password: form.password,
        });
        toast.success('Platform user created');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (u) => {
    if (!window.confirm(`Deactivate "${u.full_name}"? They will lose login access.`)) return;
    try {
      await controlApi.deactivatePlatformUser(u.id);
      toast.success('Platform user deactivated');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const filtered = users.filter(
    (u) =>
      u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const card = isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-slate-900';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-600';
  const tableRow = isDark ? 'border-slate-800 hover:bg-slate-800/50' : 'border-slate-200 hover:bg-slate-50';

  return (
    <div className="p-2" data-testid="platform-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={cn('text-2xl font-bold flex items-center gap-3', textPrimary)}>
            <ShieldCheck className="w-7 h-7 text-blue-500" />
            Platform Users
          </h1>
          <p className={cn('text-sm mt-1', textMuted)}>
            Track0tic internal staff — administrators, support, engineers
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchUsers} data-testid="platform-users-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="add-platform-user-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Add Platform User
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className={cn('rounded-xl border p-4', card)}>
          <p className={cn('text-xs uppercase tracking-wider', textMuted)}>Total</p>
          <p className={cn('text-2xl font-bold mt-1', textPrimary)}>{users.length}</p>
        </div>
        <div className={cn('rounded-xl border p-4', card)}>
          <p className={cn('text-xs uppercase tracking-wider', textMuted)}>Active</p>
          <p className="text-2xl font-bold text-green-500 mt-1">
            {users.filter((u) => u.status === 'active').length}
          </p>
        </div>
        <div className={cn('rounded-xl border p-4', card)}>
          <p className={cn('text-xs uppercase tracking-wider', textMuted)}>Inactive</p>
          <p className="text-2xl font-bold text-slate-500 mt-1">
            {users.filter((u) => u.status !== 'active').length}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or email..."
          className={cn('pl-10', isDark ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-slate-200')}
          data-testid="platform-users-search"
        />
      </div>

      {/* Table */}
      <div className={cn('rounded-xl border overflow-hidden', card)}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className={tableRow}>
                <TableHead className={textMuted}>User</TableHead>
                <TableHead className={textMuted}>Email</TableHead>
                <TableHead className={textMuted}>Phone</TableHead>
                <TableHead className={textMuted}>Status</TableHead>
                <TableHead className={textMuted}>Created</TableHead>
                <TableHead className={cn(textMuted, 'text-right')}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className={cn('text-center py-12', textMuted)}>
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No platform users found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((u) => (
                  <TableRow key={u.id} className={tableRow} data-testid={`platform-user-row-${u.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-semibold text-sm">
                          {u.full_name?.charAt(0)?.toUpperCase() || 'T'}
                        </div>
                        <span className={cn('font-medium', textPrimary)}>{u.full_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className={textMuted}>{u.email}</TableCell>
                    <TableCell className={textMuted}>{u.phone || '—'}</TableCell>
                    <TableCell>
                      <Badge variant={u.status === 'active' ? 'default' : 'secondary'}>
                        {u.status}
                      </Badge>
                    </TableCell>
                    <TableCell className={cn('text-sm', textMuted)}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(u)}
                          className="h-8 w-8 text-slate-400 hover:text-blue-500"
                          data-testid={`edit-platform-user-${u.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeactivate(u)}
                          className="h-8 w-8 text-slate-400 hover:text-red-500"
                          disabled={u.status !== 'active'}
                          data-testid={`deactivate-platform-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={cn('max-w-md', isDark ? 'bg-slate-900 border-slate-800' : 'bg-white')}>
          <DialogHeader>
            <DialogTitle className={textPrimary}>
              {editing ? 'Edit Platform User' : 'Add Platform User'}
            </DialogTitle>
            <DialogDescription className={textMuted}>
              {editing
                ? 'Update this Track0tic internal user.'
                : 'Create a new Track0tic internal user (admin/support/engineer).'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-3">
            <div>
              <Label className={textMuted}>Full Name *</Label>
              <Input
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                placeholder="Jane Doe"
                className="mt-1"
                data-testid="platform-user-name"
              />
            </div>
            <div>
              <Label className={textMuted}>Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@trackotic.com"
                className="mt-1"
                disabled={!!editing}
                data-testid="platform-user-email"
              />
            </div>
            <div>
              <Label className={textMuted}>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+91 9876543210"
                className="mt-1"
                data-testid="platform-user-phone"
              />
            </div>
            <div>
              <Label className={textMuted}>
                Password {editing ? '(leave empty to keep current)' : '*'}
              </Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                className="mt-1"
                data-testid="platform-user-password"
              />
            </div>
            {editing && (
              <div>
                <Label className={textMuted}>Status</Label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className={cn(
                    'w-full mt-1 rounded-md border px-3 py-2 text-sm',
                    isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-900'
                  )}
                  data-testid="platform-user-status"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="ghost" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="save-platform-user-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editing ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
